import PrivacyContent from "@/components/legal/PrivacyContent";
import TermsContent from "@/components/legal/TermsContent";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { getAuthControllerGetCurrentUserQueryKey } from "@/lib/lutely-api/gen/auth/auth";
import {
  acceptPolicies,
  userRequiresPolicyConsent,
} from "@/lib/lutely-api/hooks/usePolicyConsent";
import * as Dialog from "@radix-ui/react-dialog";
import { Button, Flex, Text, Theme } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { toast } from "react-toastify";

// Same descendant styling LegalDocument gives the /terms and /privacy pages,
// so the documents read identically inside the modal.
const legalTextClasses = `text-[13px] leading-[1.7] text-text-secondary
  [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:text-black [&_h2]:mt-[28px] [&_h2]:mb-[10px]
  [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:text-black [&_h3]:mt-[18px] [&_h3]:mb-[8px]
  [&_p]:mb-[12px]
  [&_ul]:list-disc [&_ul]:pl-[22px] [&_ul]:mb-[12px]
  [&_ol]:list-decimal [&_ol]:pl-[22px] [&_ol]:mb-[12px]
  [&_li]:mb-[6px]
  [&_strong]:text-black
  [&_a]:text-brand-primary [&_a]:underline`;

/**
 * Blocking re-consent modal (LUT-941): existing users whose account predates
 * the current policy bundle must read and accept it before continuing. No
 * dismiss, no click-through — Agree only unlocks after scrolling both
 * documents to the end, and both render in full inside the modal (no
 * navigation away).
 */
export default function PolicyConsentModal() {
  const { data: user } = useLoggedInUser();
  const queryClient = useQueryClient();
  // QA hook: ?preview=policy-consent forces the modal open after acceptance.
  const search = useSearch({ strict: false }) as { preview?: string };
  const forcedPreview = search.preview === "policy-consent";

  const [isAccepting, setIsAccepting] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);
  const [hasReadAll, setHasReadAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  const open =
    !justAccepted &&
    (userRequiresPolicyConsent(user) || (forcedPreview && !!user));

  const checkScrolledToEnd = useCallback(() => {
    const el = scrollRef.current;
    // A 0-height box means the modal hasn't laid out yet — measuring it now
    // would false-positive as "fully read".
    if (!el || !el.clientHeight) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      setHasReadAll(true);
    }
  }, []);

  // Mobile browsers ignore ::-webkit-scrollbar styling (overlay bars hide
  // between scrolls), so a DIY thumb keeps a visible scroll cue on touch
  // devices; the track is hidden under pointer:fine where the native custom
  // scrollbar already stays visible. The cue is visible by default via CSS —
  // this only refines thumb size/position once real dimensions exist.
  const updateScrollIndicator = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb || !el.clientHeight) return;
    const scrollable = el.scrollHeight > el.clientHeight + 1;
    track.style.opacity = scrollable ? "1" : "0";
    if (!scrollable) return;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(
      (el.clientHeight / el.scrollHeight) * trackHeight,
      32,
    );
    const progress = el.scrollTop / (el.scrollHeight - el.clientHeight);
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${progress * (trackHeight - thumbHeight)}px)`;
  }, []);

  const handleScroll = () => {
    checkScrolledToEnd();
    updateScrollIndicator();
  };

  // Scroll events get swallowed in some environments (in-app browsers), so an
  // end-sentinel observer plus periodic rechecks back up the onScroll path —
  // otherwise a user who can't register scrolls is locked out of the app.
  useEffect(() => {
    if (!open) return;
    checkScrolledToEnd();
    updateScrollIndicator();
    const el = scrollRef.current;
    const end = endRef.current;
    if (!el || !end) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setHasReadAll(true);
      },
      { root: el },
    );
    io.observe(end);
    const ro = new ResizeObserver(() => {
      checkScrolledToEnd();
      updateScrollIndicator();
    });
    ro.observe(el);
    const interval = window.setInterval(() => {
      checkScrolledToEnd();
      updateScrollIndicator();
    }, 500);
    return () => {
      io.disconnect();
      ro.disconnect();
      window.clearInterval(interval);
    };
  }, [open, checkScrolledToEnd, updateScrollIndicator]);

  const handleAgree = async () => {
    setIsAccepting(true);
    try {
      await acceptPolicies();
      setJustAccepted(true);
      await queryClient.invalidateQueries({
        queryKey: getAuthControllerGetCurrentUserQueryKey(),
      });
    } catch {
      toast.error("We couldn't record your acceptance. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[1100]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-[720px] bg-[#fafbfd] rounded-[12px] shadow-[0px_14px_20px_-1px_rgba(5,12,43,0.14)] z-[1100] outline-none"
          // Fully blocking: no Esc, no outside click, no close button.
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          data-testid="policy-consent-modal"
        >
          <Theme>
            {/* 85dvh (inline) tracks the visible viewport on mobile; the class
                is the fallback where dvh is unsupported. */}
            <Flex
              direction="column"
              className="max-h-[85vh]"
              style={{ maxHeight: "85dvh" }}
            >
              <Flex
                direction="column"
                className="flex-none px-[28px] pt-[24px] gap-[6px]"
              >
                <Dialog.Title asChild>
                  <Text className="font-semibold text-[20px] text-black">
                    We&apos;ve updated our Terms &amp; Privacy Policy
                  </Text>
                </Dialog.Title>
                <Dialog.Description asChild>
                  <Text className="text-[14px] text-text-secondary">
                    Please review the updated Terms of Use and Privacy Policy
                    below and accept them to keep using Lutely.
                  </Text>
                </Dialog.Description>
              </Flex>

              <div className="relative mx-[28px] my-[16px] flex-1 min-h-[240px] flex flex-col">
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  tabIndex={0}
                  data-testid="policy-consent-scroll"
                  // overflow-y-scroll + custom webkit scrollbar: opts out of
                  // the auto-hiding overlay scrollbars so users can tell they
                  // must scroll to reach the end (and unlock Agree). On coarse
                  // pointers the native bar hides entirely — the DIY cue below
                  // is the only indicator there, avoiding doubled scrollbars.
                  // touch-pan-y keeps touch scrolling alive when a parent
                  // handler grabs gestures; tabIndex lets keyboard users
                  // scroll it as a fallback.
                  // outline only on focus-visible: the dialog auto-focuses this
                  // box (for keyboard scrolling), which otherwise paints the
                  // native focus ring on open.
                  className="flex-1 min-h-0 overflow-y-scroll touch-pan-y overscroll-contain rounded-[8px] border border-[rgba(5,12,43,0.1)] bg-white px-[20px] py-[16px] outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[rgba(5,12,43,0.06)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[rgba(5,12,43,0.3)] hover:[&::-webkit-scrollbar-thumb]:bg-[rgba(5,12,43,0.45)] [@media(pointer:coarse)]:[&::-webkit-scrollbar]:hidden [@media(pointer:coarse)]:[scrollbar-width:none]"
                  style={{
                    scrollbarColor: "rgba(5,12,43,0.35) rgba(5,12,43,0.06)",
                  }}
                >
                  <div className={legalTextClasses}>
                    <h2 className="!mt-0">Terms of Use</h2>
                    <TermsContent />
                    <hr className="my-[28px] border-[rgba(5,12,43,0.1)]" />
                    <h2>Privacy Policy</h2>
                    <PrivacyContent />
                  </div>
                  <div ref={endRef} className="h-px" />
                </div>
                {/* Always-visible scroll cue for touch devices. */}
                <div
                  ref={trackRef}
                  aria-hidden="true"
                  data-testid="policy-consent-scroll-cue"
                  className="pointer-events-none absolute right-[5px] top-[12px] bottom-[12px] w-[5px] rounded-full bg-[rgba(5,12,43,0.08)] [@media(pointer:fine)]:hidden"
                >
                  <div
                    ref={thumbRef}
                    className="h-[64px] w-full rounded-full bg-[rgba(5,12,43,0.35)]"
                  />
                </div>
                {/* Tap-to-scroll fallback for iOS Safari, where the dialog's
                    scroll lock can eat touch gestures on the inner box —
                    programmatic scrolling still works there. */}
                {!hasReadAll && (
                  <button
                    type="button"
                    aria-label="Scroll to the end"
                    data-testid="policy-consent-scroll-down"
                    onClick={() => {
                      const el = scrollRef.current;
                      el?.scrollTo({
                        top: el.scrollHeight,
                        behavior: "smooth",
                      });
                    }}
                    className="absolute bottom-[12px] right-[18px] flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[rgba(5,12,43,0.1)] bg-white text-[24px] text-text-primary shadow-[0px_4px_10px_rgba(5,12,43,0.18)] [@media(pointer:fine)]:hidden"
                  >
                    <MdKeyboardArrowDown />
                  </button>
                )}
              </div>

              <Flex className="flex-none items-center justify-between gap-3 px-[28px] pb-[20px]">
                <Text className="text-[12px] text-text-secondary">
                  {hasReadAll
                    ? "Thanks for reading."
                    : "Scroll to the end of both documents to enable Agree."}
                </Text>
                <Button
                  size="3"
                  radius="full"
                  disabled={!hasReadAll || isAccepting}
                  onClick={handleAgree}
                  data-testid="policy-consent-agree"
                  className="cursor-pointer"
                >
                  {isAccepting ? "Saving..." : "I agree"}
                </Button>
              </Flex>
            </Flex>
          </Theme>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
